import { getApiBaseUrl, handleApiError } from '../../../core/config/api';
import { BaseUserService } from './baseService';
import type {
  Referent,
  ReferentFunction,
  ReferentTypeDefinition,
  ReferentFunctionDefinition,
  InvitationCodeRow,
} from './types';

/** Sous-service extrait de userService.ts : gestion des référents (types/définitions/assignations/codes d'invitation). */

export class UserReferentsService extends BaseUserService {
  async getMyReferents(): Promise<{ success: boolean; data: Referent[]; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/me/referents`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async getUserReferents(
    userId: number
  ): Promise<{ success: boolean; data: Referent[]; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/referents`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async assignReferent(
    userId: number,
    referentFunction: string,
    description?: string
  ): Promise<{ success: boolean; message: string; data: Referent; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/referents`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({ function: referentFunction, description }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async removeReferent(
    userId: number,
    referentFunction: string
  ): Promise<{ success: boolean; message: string; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/referents/${encodeURIComponent(referentFunction)}`;
      const response = await this.fetchWithAuth(url, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async getReferentTypes(): Promise<{
    success: boolean;
    data: ReferentTypeDefinition[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async createReferentType(body: {
    type_key: string;
    name: string;
    description?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    data: ReferentTypeDefinition;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async updateReferentType(
    id: number,
    body: Partial<
      Pick<ReferentTypeDefinition, 'type_key' | 'name' | 'description' | 'is_active'>
    >
  ): Promise<{
    success: boolean;
    message?: string;
    data: ReferentTypeDefinition;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types/${id}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async deactivateReferentType(
    id: number
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types/${id}`;
      const response = await this.fetchWithAuth(url, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async getReferentDefinitions(): Promise<{
    success: boolean;
    data: ReferentFunctionDefinition[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async createReferentDefinition(body: {
    function_key: string;
    name: string;
    description?: string;
    referent_type_id: number;
  }): Promise<{
    success: boolean;
    message?: string;
    data: ReferentFunctionDefinition;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async updateReferentDefinition(
    id: number,
    body: Partial<
      Pick<ReferentFunctionDefinition, 'name' | 'description' | 'referent_type_id' | 'is_active'>
    >
  ): Promise<{
    success: boolean;
    message?: string;
    data: ReferentFunctionDefinition;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions/${id}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async deactivateReferentDefinition(
    id: number
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions/${id}`;
      const response = await this.fetchWithAuth(url, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async getReferentFunctions(
    referentType?: string
  ): Promise<{ success: boolean; data: ReferentFunction[]; error?: string }> {
    try {
      const url = referentType
        ? `${getApiBaseUrl(true)}/users/referents/functions?referent_type=${encodeURIComponent(
            referentType
          )}`
        : `${getApiBaseUrl(true)}/users/referents/functions`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async getInvitationCodes(): Promise<{
    success: boolean;
    data: InvitationCodeRow[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/invitation-codes`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async generateInvitationCodes(body?: {
    quantity?: number;
    expires_at?: string | null;
  }): Promise<{
    success: boolean;
    message?: string;
    data: InvitationCodeRow[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/invitation-codes/generate`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }
}
